import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Database, Cog, ArrowRight } from 'lucide-react';

const EnterpriseAdvantageShowcase = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-white via-gray-50 to-blue-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Key Differentiators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Enterprise-First Design</h4>
              <p className="text-blue-100 leading-relaxed">
                Built specifically for complex enterprise workflows with security, compliance, and scalability at the core.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Cog size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Industry-Specific AI</h4>
              <p className="text-emerald-100 leading-relaxed">
                Specialized modules for construction, engineering, and legal workflows - not generic chatbots.
              </p>
            </div>

            <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-8 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Database size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Native Integration</h4>
              <p className="text-violet-100 leading-relaxed">
                Deep document integration with version control, not just surface-level chat responses.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Results Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-gradient-to-r from-gray-900 to-blue-900 rounded-3xl p-8 md:p-12 lg:p-16 text-white mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              The Enterprise <span className="text-blue-300">Difference</span>
            </h3>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              While others focus on chat, we focus on transforming how enterprises actually work with their most critical documents and processes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { metric: '100%', label: 'Source Attribution', description: 'Every answer linked to source documents' },
              { metric: '99.99%', label: 'Uptime SLA', description: 'Enterprise-grade reliability guarantee' },
              { metric: '5', label: 'Industry Modules', description: 'Specialized AI for specific workflows' },
              { metric: 'SOC 2', label: 'Compliance Ready', description: 'Built for regulated industries' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-blue-300 mb-2">{stat.metric}</div>
                <div className="text-lg font-semibold text-white mb-2">{stat.label}</div>
                <p className="text-blue-100 text-sm">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Experience the Enterprise Difference
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            See why leading construction, engineering, and legal teams choose Ufficient over generic AI tools for their mission-critical operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 group">
                Schedule Enterprise Demo
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/features">
              <button className="inline-flex items-center px-8 py-4 bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 group">
                Compare All Features
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EnterpriseAdvantageShowcase; 